import { types, Instance, flow, toGenerator } from 'mobx-state-tree';
import { getUserFromToken } from '@/services/api/authApi';
import UserModel, { IUserModel } from '@/stores/models/UserModel';
import { getUser } from '@/services/api/userApi';

export type IUserStore = Instance<typeof UserStore>;

const UserStore = types
  .model('UserStore', {
    isLoading: false,
  })
  .volatile(() => ({
    currentUser: null as IUserModel | null,
    fetchUserPromise: null as Promise<void> | null,
  }))
  .views((self) => ({
    get isLoaded(): boolean {
      return self.currentUser !== null;
    },
    get displayName(): string {
      return self.currentUser?.displayName ?? '';
    },
  }))
  .actions((self) => ({
    fetchUserById: flow(function* (id: number) {
      try {
        yield* toGenerator(getUser(id));
      } catch (err) {
        console.warn(`[UserStore] Failed to fetch user with id ${id}:`, err);
      }
    }),
    _setFetchUserPromise(promise: Promise<void> | null) {
      self.fetchUserPromise = promise;
    },
    _fetchUserWork: flow(function* () {
      self.isLoading = true;
      try {
        const { data } = yield* toGenerator(getUserFromToken());

        if (!data || !data.user) {
          console.warn('[UserStore] No user data found in token response');
          return;
        }
        self.currentUser = UserModel.create({
          id: data.user.id,
          firstName: data.user.first_name,
          lastName: data.user.last_name,
          email: data.user.email ?? null,
          is_internal: data.user.is_internal ?? null,
          properties: data.user.properties ?? {},
          modifications: (data.user.modifications ?? []).map((um) => ({
            id: um.id,
            modification: {
              id: um.modification.id,
              unique_name: um.modification.unique_name,
              name: um.modification.name,
              workout_types: um.modification.workout_types ?? [],
            },
          })),
        });

        // Non blocking fetch by id. We do this to ensure we always perform a request
        // that updates the device data in the backend, since `getUserFromToken` follows a special
        // flow of middlewares.
        (self as IUserStore).fetchUserById(data.user.id);
      } catch (err) {
        console.error('[UserStore] Failed to fetch user:', err);
      } finally {
        self.isLoading = false;
      }
    }),

    clear() {
      self.currentUser = null;
      self.isLoading = false;
      self.fetchUserPromise = null;
    },
  }))
  .actions((self) => ({
    fetchUser(): Promise<void> {
      if (self.fetchUserPromise) {
        return self.fetchUserPromise;
      }
      const promise = (self as IUserStore)._fetchUserWork().finally(() => {
        (self as IUserStore)._setFetchUserPromise(null);
      });
      (self as IUserStore)._setFetchUserPromise(promise);
      return promise;
    },
  }));

export default UserStore;
